/**
 * InsightsFirestoreService
 *
 * Type-safe service for InsightsAnalysis entity CRUD operations.
 * Pattern: follows StrategyService from firebase-core.
 *
 * Firestore path: projects/{projectId}/insights_analyses/{analysisId}
 */
import { collection, doc, getDoc, getDocs, setDoc, query, limit, orderBy, onSnapshot, } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { getBlockLibraryCliApiBaseUrl as getCliApiBaseUrl } from '@modules/firebase-core/services/BlockLibraryMutationApi';
const DELETE_TIMEOUT_MESSAGE = 'The server did not finish the delete in time — refresh the list to see whether the analysis is gone.';
/** The owning API's refusal text; a non-JSON body (a gateway page) gets a status-based message, never a parse error. */
function deleteRefusalMessage(status, payload) {
    if (!payload || typeof payload !== 'object') {
        return [502, 503, 504].includes(status) ? DELETE_TIMEOUT_MESSAGE : `Insight analysis could not be deleted (HTTP ${status}).`;
    }
    return (typeof payload.error === 'object' ? payload.error?.message : undefined)
        ?? payload.detail
        ?? (typeof payload.error === 'string' ? payload.error : 'Insight analysis could not be deleted.');
}
export class InsightsFirestoreService {
    constructor(projectId) {
        this.firestore = CoreFirebaseService.getInstance().firestore;
        this.projectId = projectId;
    }
    getCollectionPath() {
        return `projects/${this.projectId}/insights_analyses`;
    }
    /**
     * Strip undefined values — Firestore rejects them.
     * JSON.parse(JSON.stringify()) is the most reliable approach:
     * it removes all undefined keys and handles nested structures.
     */
    static stripUndefined(obj) {
        return JSON.parse(JSON.stringify(obj));
    }
    async create(data) {
        try {
            const collectionRef = collection(this.firestore, this.getCollectionPath());
            const docRef = doc(collectionRef);
            // Build the document, then sanitize via JSON round-trip (strips undefined)
            const raw = {
                ...data,
                id: docRef.id,
                projectId: this.projectId,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            const analysisData = JSON.parse(JSON.stringify(raw));
            await setDoc(docRef, analysisData);
            return analysisData;
        }
        catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.create');
            throw error;
        }
    }
    async get(id) {
        try {
            const docRef = doc(this.firestore, this.getCollectionPath(), id);
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists())
                return null;
            return { ...docSnap.data(), id: docSnap.id };
        }
        catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.get');
            throw error;
        }
    }
    async update(id, data) {
        try {
            const docRef = doc(this.firestore, this.getCollectionPath(), id);
            // Read current doc, merge locally, full overwrite (avoids merge:true stale key bug)
            const current = await getDoc(docRef);
            if (!current.exists())
                throw new Error(`Analysis ${id} not found`);
            const merged = { ...current.data(), ...data, updatedAt: new Date().toISOString() };
            const clean = InsightsFirestoreService.stripUndefined(merged);
            await setDoc(docRef, clean);
        }
        catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.update');
            throw error;
        }
    }
    async listInputTaskSnapshots(analysisId, snapshotCollection = 'input_task_snapshots') {
        try {
            const snapshotsRef = collection(this.firestore, `${this.getCollectionPath()}/${analysisId}/${snapshotCollection}`);
            const snapshot = await getDocs(snapshotsRef);
            return snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
        }
        catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.listInputTaskSnapshots');
            throw error;
        }
    }
    async listPerTaskPackages(analysisId, packageCollection = 'per_task_packages') {
        try {
            const packagesRef = collection(this.firestore, `${this.getCollectionPath()}/${analysisId}/${packageCollection}`);
            const snapshot = await getDocs(packagesRef);
            return snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
        }
        catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.listPerTaskPackages');
            throw error;
        }
    }
    async listCategoryPackages(analysisId, packageCollection = 'category_packages') {
        try {
            const packagesRef = collection(this.firestore, `${this.getCollectionPath()}/${analysisId}/${packageCollection}`);
            const snapshot = await getDocs(packagesRef);
            return snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
        }
        catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.listCategoryPackages');
            throw error;
        }
    }
    async getTaskNames(taskIds) {
        const uniqueIds = Array.from(new Set(taskIds.map((id) => String(id || '').trim()).filter(Boolean)));
        const names = {};
        await Promise.all(uniqueIds.map(async (taskId) => {
            try {
                const taskRef = doc(this.firestore, `projects/${this.projectId}/tasks`, taskId);
                const taskSnap = await getDoc(taskRef);
                if (!taskSnap.exists())
                    return;
                const data = taskSnap.data();
                const name = typeof data.name === 'string' && data.name.trim() ? data.name.trim() : '';
                if (name)
                    names[taskId] = name;
            }
            catch (error) {
                CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.getTaskNames');
            }
        }));
        return names;
    }
    async getAudienceTaskContexts(audienceIds) {
        const uniqueIds = Array.from(new Set(audienceIds.map((id) => String(id || '').trim()).filter(Boolean)));
        const contexts = {};
        await Promise.all(uniqueIds.map(async (audienceId) => {
            try {
                const audienceRef = doc(this.firestore, `projects/${this.projectId}/audiences`, audienceId);
                const audienceSnap = await getDoc(audienceRef);
                if (!audienceSnap.exists()) {
                    contexts[audienceId] = { audienceId };
                    return;
                }
                const data = audienceSnap.data();
                const taskId = typeof data.taskContext?.taskId === 'string' && data.taskContext.taskId.trim()
                    ? data.taskContext.taskId.trim()
                    : typeof data.taskId === 'string' && data.taskId.trim()
                        ? data.taskId.trim()
                        : undefined;
                const taskName = typeof data.taskContext?.taskName === 'string' && data.taskContext.taskName.trim()
                    ? data.taskContext.taskName.trim()
                    : typeof data.taskName === 'string' && data.taskName.trim()
                        ? data.taskName.trim()
                        : undefined;
                const audienceName = typeof data.name === 'string' && data.name.trim() ? data.name.trim() : undefined;
                contexts[audienceId] = { audienceId, audienceName, taskId, taskName };
            }
            catch (error) {
                contexts[audienceId] = { audienceId };
                CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.getAudienceTaskContexts');
            }
        }));
        return contexts;
    }
    async deleteCascade(id) {
        try {
            const user = CoreFirebaseService.getInstance().auth.currentUser;
            if (!user)
                throw new Error('Sign in before deleting an insight analysis.');
            const token = await user.getIdToken();
            const response = await fetch(`${getCliApiBaseUrl().replace(/\/$/, '')}/api/cli/v1/projects/${encodeURIComponent(this.projectId)}/product/insights/${encodeURIComponent(id)}`, {
                method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
            });
            // A gateway timeout (502/503/504) answers with HTML, not problem+json (W12 Task A4, R29).
            const payload = await response.json().catch(() => null);
            if (!response.ok || payload?.success === false || payload?.data?.deleted !== true) {
                throw new Error(deleteRefusalMessage(response.status, payload));
            }
        }
        catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.deleteCascade');
            throw error;
        }
    }
    async delete(id) {
        return this.deleteCascade(id);
    }
    async deleteMany(ids) {
        const uniqueIds = Array.from(new Set(ids.map((id) => String(id || '').trim()).filter(Boolean)));
        for (const id of uniqueIds) {
            await this.deleteCascade(id);
        }
    }
    async list(options = {}) {
        try {
            const collectionRef = collection(this.firestore, this.getCollectionPath());
            const pageLimit = options.limit === null ? null : (options.limit || 50);
            const q = pageLimit
                ? query(collectionRef, orderBy('createdAt', 'desc'), limit(pageLimit))
                : query(collectionRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
        }
        catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.list');
            throw error;
        }
    }
    onCollectionSnapshot(onNext, onError, options = {}) {
        const collectionRef = collection(this.firestore, this.getCollectionPath());
        const pageLimit = options.limit === null ? null : (options.limit || 50);
        const q = pageLimit
            ? query(collectionRef, orderBy('createdAt', 'desc'), limit(pageLimit))
            : query(collectionRef, orderBy('createdAt', 'desc'));
        return onSnapshot(q, (snapshot) => {
            const analyses = snapshot.docs.map(d => ({ ...d.data(), id: d.id }));
            onNext(analyses);
        }, (error) => {
            CoreFirebaseService.getInstance().logError(error, 'InsightsFirestoreService.onCollectionSnapshot');
            onError(error);
        });
    }
}
